import { buildJobDetailPath, buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { buildStageDetailPath, buildSyntheticStageId } from '@/features/pipelines/utils/stagePaths';
import {
  buildDailyHistoryFromDetails,
  buildLatestDailyHistory,
} from '@/features/shared/components/entityDailyHistory/dailyHistoryRows';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistoryCell } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { JobListItemDto } from '@flowqa/shared';

const buildJobLatestDailyHistory = (job: JobListItemDto): DailyHistoryCell[] => {
  if (job.latestRun?.timestamp == null) {
    return [];
  }

  return buildLatestDailyHistory({
    timestamp: job.latestRun.timestamp,
    status: job.latestRun.status,
    detail: {
      status: job.latestRun.status,
      timestamp: job.latestRun.timestamp,
      runLabel: job.latestRunBuildKey ?? null,
      runHref: buildJobRunDetailPath({
        jobRunId: job.latestRun.id,
        pipelineId: job.pipelineId,
        stageName: job.stageName,
        jobName: job.jobName,
      }),
    },
  });
};

const buildJobDailyHistory = (job: JobListItemDto): DailyHistoryCell[] => {
  const details = (job.runSummaries ?? [])
    .filter(
      (
        run,
      ): run is NonNullable<JobListItemDto['runSummaries']>[number] & {
        timestamp: Date;
      } => run.timestamp instanceof Date,
    )
    .map((run) => ({
      status: run.status,
      timestamp: run.timestamp,
      runLabel: run.buildKey ?? null,
      runHref: buildJobRunDetailPath({
        jobRunId: run.id,
        pipelineId: job.pipelineId,
        stageName: job.stageName,
        jobName: job.jobName,
      }),
    }));

  return buildDailyHistoryFromDetails({
    items: details,
    getDay: (item) => item.timestamp,
    getStatus: (item) => item.status,
  });
};

export const buildJobHistoryRows = (
  jobs: JobListItemDto[] | undefined,
  isLatestMode: boolean,
): EntityDailyHistoryRow[] =>
  (jobs ?? []).map((job) => {
    const prefixParts = [
      job.pipelineId && job.pipelineName
        ? {
            label: job.pipelineName,
            href: `/pipelines/${resolvePipelineSlug({ id: job.pipelineId, name: job.pipelineName })}`,
          }
        : job.pipelineName
          ? { label: job.pipelineName, href: null }
          : null,
      job.stageName
        ? {
            label: job.stageName,
            href: buildStageDetailPath(buildSyntheticStageId(job.pipelineId, job.stageName)),
          }
        : null,
    ].filter((part): part is NonNullable<typeof part> => part !== null);

    return {
      id: job.id,
      prefixLabel: prefixParts.map((part) => part.label).join(' / '),
      prefixParts,
      name: job.jobName,
      href: buildJobDetailPath(job.id),
      dailyHistory: isLatestMode ? buildJobLatestDailyHistory(job) : buildJobDailyHistory(job),
    };
  });
